/**
 * /terceirizacao/oses/criar — Criar nova OSE em RASCUNHO.
 *
 * Usuario escolhe parceiro + categoria (auto-preenche do parceiro) +
 * descricao do servico + lista de OPs (opcional - pode adicionar depois).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiSave } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import EntityHeader from "../../../components/EntityHeader";
import { useAuth } from "../../../context/AuthContext";
import {
  CATEGORIAS,
  criarOse,
  listarParceiros,
} from "../../../services/terceirizacao";
import { PageContainer } from "../../../styles/shared";

import {
  Card,
  ErrorMsg,
  SecondaryButton,
  SectionTitle,
  Select,
  Input,
  Textarea,
} from "../../Orcamentos/styles";


export default function OseCriar() {
  const { request } = useAuth();
  const navigate = useNavigate();

  const [parceiros, setParceiros] = useState([]);
  const [carregandoParceiros, setCarregandoParceiros] = useState(false);
  const [parceiroId, setParceiroId] = useState("");
  const [categoria, setCategoria] = useState("");
  const [descricaoServico, setDescricaoServico] = useState("");
  const [valor, setValor] = useState("");
  const [opNumeros, setOpNumeros] = useState("");
  const [error, setError] = useState(null);

  // Carrega parceiros ativos no mount
  useEffect(() => {
    setCarregandoParceiros(true);
    listarParceiros(request, { ativo: true })
      .then((payload) => {
        const list = Array.isArray(payload) ? payload : payload?.results || [];
        setParceiros(list);
      })
      .catch((err) => {
        setError(err?.message || "Erro ao carregar parceiros.");
        setParceiros([]);
      })
      .finally(() => setCarregandoParceiros(false));
  }, [request]);

  // Auto-set categoria quando parceiro muda
  const handleParceiroChange = (e) => {
    const id = e.target.value;
    setParceiroId(id);
    const p = parceiros.find((x) => x.id === id);
    if (p && !categoria) {
      setCategoria(p.categoria || "");
    }
  };

  const handleCriar = useCallback(async () => {
    if (!parceiroId) {
      throw new Error("Selecione um parceiro.");
    }
    if (!descricaoServico.trim()) {
      throw new Error("Descreva o serviço a ser executado.");
    }
    const opIds = opNumeros
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const valorLimpo = valor.trim().replace(",", ".");
    if (valorLimpo && (Number.isNaN(Number(valorLimpo)) || Number(valorLimpo) < 0)) {
      throw new Error("Valor do serviço inválido.");
    }
    const dados = {
      parceiro_id: parceiroId,
      categoria: categoria || undefined,
      descricao_servico: descricaoServico.trim(),
      valor: valorLimpo || null,
      op_ids: opIds,
    };
    const ose = await criarOse(request, dados);
    navigate(`/terceirizacao/oses/${ose.id}`);
  }, [request, parceiroId, categoria, descricaoServico, valor, opNumeros, navigate]);

  return (
    <PageContainer>
      <EntityHeader
        title="Nova OSE"
        subtitle="Criar lote de peças para envio ao terceiro (Fase 10)"
        actions={[
          {
            label: "Voltar",
            icon: <FiArrowLeft size={14} aria-hidden="true" />,
            onClick: () => navigate("/terceirizacao/oses"),
          },
        ]}
      />

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <Card>
        <SectionTitle>Dados da OSE</SectionTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Parceiro *</span>
            <Select
              value={parceiroId}
              onChange={handleParceiroChange}
              disabled={carregandoParceiros}
            >
              <option value="">
                {carregandoParceiros ? "Carregando..." : "Selecione um parceiro"}
              </option>
              {parceiros.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome} ({p.categoria_display || p.categoria}) - SLA {p.sla_dias}d
                </option>
              ))}
            </Select>
            {parceiros.length === 0 && !carregandoParceiros && (
              <small style={{ color: "var(--danger, #D32F2F)" }}>
                Nenhum parceiro cadastrado. Cadastre em "/terceirizacao/parceiros" primeiro.
              </small>
            )}
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Categoria</span>
            <Select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
            >
              <option value="">Auto (espelha do parceiro)</option>
              {CATEGORIAS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </Select>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Descrição do serviço *</span>
            <Textarea
              rows={3}
              value={descricaoServico}
              onChange={(e) => setDescricaoServico(e.target.value)}
              placeholder="Ex: Laquear 12 portas - branco TX, acabamento fosco"
              maxLength={500}
            />
            <small style={{ opacity: 0.6 }}>
              {descricaoServico.length}/500 caracteres
            </small>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Valor do serviço (R$)</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="Ex: 1250.00"
            />
            <small style={{ opacity: 0.6 }}>
              Custo acordado com o parceiro. Pode ficar em branco agora e ser
              preenchido depois — mas sem ele a aprovação no CQ não gera a
              conta a pagar, e a margem do pedido sai inflada.
            </small>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>OPs vinculadas (opcional)</span>
            <Input
              value={opNumeros}
              onChange={(e) => setOpNumeros(e.target.value)}
              placeholder="Cole IDs das OPs separados por vírgula ou espaço"
            />
            <small style={{ opacity: 0.6 }}>
              Você pode adicionar OPs depois pelo detalhe. Necessário antes de enviar ao terceiro.
            </small>
          </label>
        </div>
      </Card>

      <Card>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <SecondaryButton onClick={() => navigate("/terceirizacao/oses")}>
            Cancelar
          </SecondaryButton>
          <AsyncButton
            variant="primary"
            onClick={handleCriar}
            successMessage="OSE criada em RASCUNHO"
          >
            <FiSave size={14} /> Criar OSE
          </AsyncButton>
        </div>
      </Card>
    </PageContainer>
  );
}
